import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BusinessIcon from '@mui/icons-material/Business';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';

export function TenantSwitcher() {
  const { t } = useTranslation();
  const { user, currentTenant, switchTenant } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (!user || user.tenants.length === 0) return null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (user.tenants.length === 1) return;
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (tenantId: string) => {
    handleClose();
    if (tenantId !== currentTenant?.id) switchTenant(tenantId);
  };

  const single = user.tenants.length === 1;
  const label = currentTenant?.name ?? t('tenant.unknown', { defaultValue: 'Unbekannter Mandant' });

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }}>
      <Tooltip title={single
        ? t('tenant.singleTooltip', { defaultValue: 'Aktiver Mandant' })
        : t('tenant.switchTooltip', { defaultValue: 'Mandant wechseln' })}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<BusinessIcon fontSize="small" />}
          endIcon={!single ? <KeyboardArrowDownIcon fontSize="small" /> : null}
          onClick={handleClick}
          sx={{
            textTransform: 'none',
            borderColor: 'divider',
            color: 'text.primary',
            fontWeight: 500,
            maxWidth: 200,
            '& .MuiButton-startIcon': { mr: 0.5 },
            '& .MuiButton-endIcon': { ml: 0.5 },
          }}
          data-testid="tenant-switcher"
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ maxWidth: 140, fontWeight: 500 }}
          >
            {label}
          </Typography>
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t('tenant.menuTitle', { defaultValue: 'Mandant wechseln' })}
          </Typography>
        </Box>
        <Divider />
        {user.tenants.map((tenant) => {
          const isActive = tenant.id === currentTenant?.id;
          return (
            <MenuItem
              key={tenant.id}
              selected={isActive}
              onClick={() => handleSelect(tenant.id)}
              data-testid={`tenant-option-${tenant.id}`}
            >
              <ListItemIcon>
                {isActive ? <CheckIcon fontSize="small" color="primary" /> : <BusinessIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={tenant.name} />
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
